import { SetStateAction, useCallback, useEffect, useState } from 'react'
import {
  MotivationInterface,
  MotivationRuleInterface,
  ValidationResultInterface,
  ValidationRulesInterface,
  OptionItemInterface,
} from '../../../../../../common/types/interfaces'
import { getSalesApi } from '../../../../../../salesApi/salesApi'
import { useValidation } from '../../../../../../common/hooks/useValidation'
import { motivationValidations } from '../../../../../../common/utils/constants'
import { useInput } from '../../../../../../common/hooks/useInput'
import { useCommonLoader } from '../../../../../../common/hooks/useCommonLoader'
import { delayAction } from '../../../../../../common/utils/functions/delayAction'

const validations: ValidationRulesInterface[] = motivationValidations

export const useForm = (
  data: MotivationInterface,
  setData: React.Dispatch<React.SetStateAction<MotivationInterface>>,
  edit: boolean,
  getList: () => void,
  setModalActive: React.Dispatch<SetStateAction<boolean>>,
  el?: MotivationInterface,
) => {
  const [salesApi] = useState(getSalesApi())
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[]>([])
  const [disabled, setDisabled] = useState(true)
  const [view, setView] = useState<'main' | 'rules'>('main')
  const [categoryList, setCategoryList] = useState<any[]>([])
  const [saleItemList, setSaleItemList] = useState<any[]>([])

  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )
  const { inputHandler } = useInput(setData)
  const { showCommonLoader, hideCommonLoader } = useCommonLoader()

  const getCategoryList = useCallback(async () => {
    try {
      const result = await salesApi.getCategoryList()
      if (result) setCategoryList(result?.data)
    } catch (error) {}
  }, [salesApi])

  const getSaleItemList = useCallback(async () => {
    try {
      const result = await salesApi.getSaleItemList()
      if (result) setSaleItemList(result?.data)
    } catch (error) {}
  }, [salesApi])

  const createCategorySelectList = useCallback((): OptionItemInterface[] => {
    return categoryList.map((item) => ({
      id: String(item.id),
      label: item.name,
      name: item.name,
      value: String(item.id),
    }))
  }, [categoryList])

  const createSaleItemSelectList = useCallback((): OptionItemInterface[] => {
    return saleItemList.map((item) => ({
      id: String(item.id),
      label: item.name,
      name: item.name,
      value: String(item.id),
    }))
  }, [saleItemList])

  useEffect(() => {
    if (el) setData(el)
  }, [el, setData])

  useEffect(() => {
    getCategoryList()
    getSaleItemList()
  }, [getCategoryList, getSaleItemList])

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  const handleRuleAdd = () => {
    const newRule: MotivationRuleInterface = {
      categoryIds: [],
      itemIds: [],
    }
    setData((prev) => ({ ...prev, rules: [...(prev.rules || []), newRule] }))
  }

  const handleRuleRemove = (index: number) => {
    setData((prev) => ({ ...prev, rules: (prev.rules || []).filter((_, i) => i !== index) }))
  }

  const handleRuleChange = (
    index: number,
    field: keyof MotivationRuleInterface,
    value: number[] | number | undefined,
  ) => {
    setData((prev) => {
      const newRules = [...(prev.rules || [])]
      if (field === 'categoryIds' || field === 'itemIds') {
        newRules[index][field] = value as number[]
      } else if (field === 'costPerOrder' || field === 'revenuePercent') {
        newRules[index][field] = value as number
      }
      return { ...prev, rules: newRules }
    })
  }

  const formAction = useCallback(async () => {
    try {
      await showCommonLoader()
      await delayAction(1000)

      const { id, createdAt, updatedAt, deleted, metadata, ...rest } = data

      const transformedRules = (data.rules || []).map((rule) => ({
        costPerOrder: rule.costPerOrder,
        revenuePercent: rule.revenuePercent,
        categoryIds: rule.categoryIds || [],
        itemIds: rule.itemIds || [],
      }))

      const payload = {
        ...rest,
        rules: transformedRules,
      }

      if (edit && data.id) {
        // await salesApi.updateMotivation({ ...payload, id: data.id })
      } else {
        await salesApi.createMotivation(payload)
      }

      await getList()
      await hideCommonLoader()
      setModalActive(false)
    } catch (error) {
      await hideCommonLoader()
      console.error('Form action error:', error)
    }
  }, [salesApi, data, edit, getList, setModalActive, showCommonLoader, hideCommonLoader])

  useEffect(() => {
    for (const item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        checkAndAddValidation(item)
      }
    }
  }, [checkAndAddValidation, data])

  useEffect(() => {
    for (const item in data) {
      if (validations.map((el) => el.fieldName).includes(item)) {
        validate(item, data[item as keyof MotivationInterface])
      }
    }
  }, [data])

  useEffect(() => {
    setDisabled(validationResult.some((el) => el.notValid))
  }, [validationResult])

  return {
    formAction,
    inputHandler,
    setDirty,
    getValidationResult,
    disabled,
    view,
    setView,
    rules: data.rules || [],
    handleRuleAdd,
    handleRuleRemove,
    handleRuleChange,
    createCategorySelectList,
    createSaleItemSelectList,
  }
}
